import React, { useCallback, useEffect, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { Channel, UserResponse } from 'stream-chat'
import { ChannelPreviewUIComponentProps, ChatContextValue, useChatContext } from 'stream-chat-react'
import { useUnreadContext } from 'components/Chat/contexts/UnreadContext'
import type { StreamChatGenerics } from 'components/Chat/types'
import { getChannelName, getInitials } from 'components/Chat/helper'
import { isGroupChannel } from 'components/Chat/utils/channelHelpers'
import 'components/Chat/components/MessagingChannelPreview/MessagingChannelPreview.css'
import isEmpty from 'lodash/isEmpty'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { formatMessageTextPlain } from 'components/Chat/helper/messageFormatter'
import { Image as ImageIcon, Paperclip, Video as VideoIcon, AudioLines } from 'lucide-react'
import DOMPurify from 'dompurify'

type Props = ChannelPreviewUIComponentProps & {
  channel: Channel
  searchChannel: string
  usersAssessmentResponseByQuestion?: any
  chatUnreadMessageCountAction: any
  setShowChannelPreview?: any
  showChannelCreate?: boolean
  setShowChannelCreate: Dispatch<SetStateAction<boolean>>
  setActiveChannel?: ChatContextValue['setActiveChannel']
  channelMatchingMessages?: Map<string, any>
  isSearching?: boolean
  setInitialJumpMessageId?: (messageId: string | null) => void
}

type ExtendedResponse = UserResponse & {
  firstName?: string
  lastName?: string
}

type ExtendedUserResponse<T extends StreamChatGenerics> = UserResponse<T> & ExtendedResponse

/**
 * Helper function to sanitize HTML
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string
 */
const sanitizeHtml = (html: string): string => {
  if (!html) return ''

  try {
    // Sanitize with DOMPurify
    const sanitized = DOMPurify.sanitize(html, {
      ADD_TAGS: ['mention'],
      ADD_ATTR: ['id', 'data-mention-id'],
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 's', 'a', 'mention', 'span'],
      ALLOWED_ATTR: ['href', 'id', 'data-mention-id', 'class'],
    })

    return sanitized
  } catch (error) {
    console.error('Error sanitizing HTML:', error)
    // Fallback: return empty string
    return ''
  }
}

/**
 * Helper function to strip HTML tags and convert to plain text (used for search)
 * @param html - HTML string to convert
 * @returns Plain text string
 */
const stripHtmlTags = (html: string): string => {
  if (!html) return ''

  try {
    // Create a temporary div element to parse HTML
    const tmp = document.createElement('div')
    tmp.innerHTML = html

    // Get text content (this automatically strips all HTML tags)
    let text = tmp.textContent || tmp.innerText || ''

    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim()

    return text
  } catch (error) {
    console.error('Error stripping HTML tags:', error)
    // Fallback: use regex to strip HTML tags
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  }
}

/**
 * Get formatted message content for preview
 * @param message - The message object
 * @returns Object with HTML and text content
 */
const getMessagePreviewContent = (message: any): { html?: string; text?: string } => {
  // Priority 1: Use rich text HTML if available
  if (message.custom?.richTextHtml) {
    return { html: sanitizeHtml(message.custom.richTextHtml) }
  }

  // Priority 2: Use message HTML if available
  if (message.html) {
    return { html: sanitizeHtml(message.html) }
  }

  // Priority 3: Use plain text with mention formatting
  if (message.text) {
    return { text: formatMessageTextPlain(message.text) }
  }

  return {}
}

const MessagingChannelPreview = (props: Props) => {
  const {
    channel,
    setActiveChannel: propsSetActiveChannel,
    showChannelCreate,
    setShowChannelCreate,
    unread,
    searchChannel,
    channelMatchingMessages,
    setInitialJumpMessageId,
  } = props
  const {
    channel: activeChannel,
    client,
    setActiveChannel: contextSetActiveChannel,
  } = useChatContext<StreamChatGenerics>()
  const { channelMessageCount, setChatsUnreadCount, setChannelMessageCount } = useUnreadContext()
  const [members, setMembers]: any = useState([])

  // Use setActiveChannel from props or context
  const setActiveChannel = propsSetActiveChannel || contextSetActiveChannel

  const isActiveChannel = channel?.id === activeChannel?.id
  const [isChannelDisplay, setIsChannelDisplay] = useState(true)

  const channelMessageCounts = !isEmpty(channelMessageCount)
    ? channelMessageCount?.[channel.cid]
    : 0

  useEffect(() => {
    const findMembers = Object.values(channel.state.members).filter(
      ({ user }) => user?.id !== client.userID
    )
    if (findMembers) {
      setMembers(findMembers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUnreadCounts = () => {
    if (unread) {
      setChatsUnreadCount((chatsUnreadCount) => chatsUnreadCount - unread)
    }
  }

  const removeUnreadMessage = () => {
    setChannelMessageCount((state) => ({
      ...state,
      [channel.cid]: 0,
    }))
  }

  const searchByUserId = (): boolean => {
    for (const userId in channel?.state?.members) {
      let searchQuery = searchChannel.toLowerCase()
      let name: string = ''

      const user: ExtendedUserResponse<StreamChatGenerics> | undefined =
        channel.state.members[userId]?.user
      if (user?.name) {
        name = user.name.toLowerCase()
        searchQuery = searchQuery.toLowerCase()

        if (name?.includes(searchQuery)) {
          return true
        }
      } else {
        let firstName = (user?.firstName || '').toLowerCase()
        let lastName = (user?.lastName || '').toLowerCase()
        name = firstName + lastName
        if (name?.includes(searchQuery)) {
          return true
        }
      }
    }
    return false
  }

  const searchByBusinessName = useCallback((): boolean => {
    const users = Object.values(channel?.state?.members).map((member) => member.user) || []
    const businessNames = users.map((user) => user?.businessName || '') as string[]

    for (const business of businessNames) {
      if (business.toLowerCase().includes(searchChannel.toLowerCase())) return true
    }
    return false
  }, [channel?.state?.members, searchChannel])

  useEffect(() => {
    if (channel) {
      // If we have a search query, check if this channel has matching messages
      if (searchChannel.trim()) {
        // If we have matching messages for this channel, show it
        if (channelMatchingMessages && channelMatchingMessages.has(channel.cid)) {
          setIsChannelDisplay(true)
        } else {
          // Also check for user/business name matches as fallback
          const userIdFound = searchByUserId()
          const businessNameFound = searchByBusinessName()
          setIsChannelDisplay(userIdFound || businessNameFound)
        }
      } else {
        // No search query, show all channels
        setIsChannelDisplay(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchChannel, channelMatchingMessages])

  const getLastMessage = () => {
    try {
      // Show highlighted search preview if there's a search query and matching messages
      // Check for search query and matching messages regardless of isSearching state
      if (searchChannel.trim() && channelMatchingMessages?.has(channel.cid)) {
        const messages = channelMatchingMessages.get(channel.cid) || []
        if (messages.length > 0) {
          const msg = messages[0]
          const text = msg.text || ''

          // If there's no text, check for attachments
          if (!text && msg.attachments && msg.attachments.length > 0) {
            const attachment = msg.attachments[0]
            if (attachment.type === 'image') {
              return (
                <span className="inline-flex items-center gap-1 text-app-grey-80">
                  <ImageIcon className="w-4 h-4" />
                  Image
                </span>
              )
            } else if (attachment.type === 'file') {
              return (
                <span className="inline-flex items-center gap-1 text-app-grey-80">
                  <Paperclip className="w-4 h-4" />
                  File
                </span>
              )
            } else if (attachment.type === 'video') {
              return (
                <span className="inline-flex items-center gap-1 text-app-grey-80">
                  <VideoIcon className="w-4 h-4" />
                  Video
                </span>
              )
            } else if (attachment.type === 'audio') {
              return (
                <span className="inline-flex items-center gap-1 text-app-grey-80">
                  <AudioLines className="w-4 h-4" />
                  Audio
                </span>
              )
            }
          }

          // Render text with highlighted search term
          if (text) {
            // Get preview content (HTML or text)
            const previewContent = getMessagePreviewContent(msg)

            // For search results, convert HTML to plain text and highlight
            if (previewContent.html) {
              const plainText = stripHtmlTags(previewContent.html)
              return highlightSearchTerm(plainText, searchChannel)
            } else if (previewContent.text) {
              return highlightSearchTerm(previewContent.text, searchChannel)
            }
          }
        }
      }

      // Default behavior: show last message (skip deleted messages)
      const messages = channel.state.messages
      if (messages && messages.length > 0) {
        // Find the last non-deleted message
        const nonDeletedMessages = messages.filter(
          (msg) => !msg.deleted_at && msg.type !== 'deleted'
        )

        if (nonDeletedMessages.length === 0) {
          return <span>No recent activity</span>
        }

        const lastMessage = nonDeletedMessages[nonDeletedMessages.length - 1]

        // Handle different message types
        if (lastMessage.attachments && lastMessage.attachments.length > 0) {
          const attachment = lastMessage.attachments[0]
          if (attachment.type === 'image') {
            return (
              <span className="inline-flex items-center gap-1 text-app-grey-80">
                <ImageIcon className="w-4 h-4" />
                Image
              </span>
            )
          } else if (attachment.type === 'file') {
            return (
              <span className="inline-flex items-center gap-1 text-app-grey-80">
                <Paperclip className="w-4 h-4" />
                File
              </span>
            )
          } else if (attachment.type === 'video') {
            return (
              <span className="inline-flex items-center gap-1 text-app-grey-80">
                <VideoIcon className="w-4 h-4" />
                Video
              </span>
            )
          } else if (attachment.type === 'audio') {
            return (
              <span className="inline-flex items-center gap-1 text-app-grey-80">
                <AudioLines className="w-4 h-4" />
                Audio
              </span>
            )
          }
        }

        // Get the formatted preview content (HTML or text)
        const previewContent = getMessagePreviewContent(lastMessage)

        if (previewContent.html) {
          return (
            <span
              className="inline [&_strong]:font-semibold [&_em]:italic [&_s]:line-through [&_mention]:text-app-primary-80 [&_mention]:font-medium [&_p]:inline [&_br]:hidden"
              dangerouslySetInnerHTML={{ __html: previewContent.html }}
            />
          )
        }

        if (previewContent.text) {
          return <span>{previewContent.text}</span>
        }

        return <span>No recent activity</span>
      }
      return <span>No recent activity</span>
    } catch (err) {
      return <span>No recent activity</span>
    }
  }

  // Helper function to highlight search terms in text
  // This function splits the text and highlights matches
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) {
      return <span>{text}</span>
    }

    try {
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')
      const parts = text.split(regex)

      return (
        <span>
          {parts.map((part, index) => {
            // Check if this part matches the search term (case insensitive)
            // Since we split by the regex, every other part should be a match
            const isMatch = part && index % 2 === 1
            if (isMatch) {
              // Highlight the matching part
              return (
                <span
                  key={index}
                  className="bg-app-yellow-40 text-app-black-100 rounded font-medium px-0.5"
                >
                  {part}
                </span>
              )
            }
            return <span key={index}>{part}</span>
          })}
        </span>
      )
    } catch (err) {
      return <span>{text}</span>
    }
  }

  return (
    <>
      {isChannelDisplay && (
        <div
          className={`relative cursor-pointer transition-colors duration-150 rounded-lg ${
            isActiveChannel && !showChannelCreate ? 'bg-app-grey-10' : 'hover:bg-app-grey-10'
          }`}
          onClick={async () => {
            if (setActiveChannel) {
              // Handle jump to matching message if there's a search query and matching messages
              if (
                searchChannel.trim() &&
                channelMatchingMessages &&
                channelMatchingMessages.has(channel.cid) &&
                setInitialJumpMessageId
              ) {
                const messages = channelMatchingMessages.get(channel.cid)
                if (messages && messages.length > 0 && messages[0].id) {
                  setInitialJumpMessageId(messages[0].id)
                }
              }

              setShowChannelCreate(false)
              handleUnreadCounts()
              setActiveChannel(channel)
              removeUnreadMessage()

              await channel.markRead()
            }
          }}
        >
          <div className="flex items-center px-6 py-2.5 space-x-3">
            {/* Avatar Section */}
            <div className="flex-shrink-0 relative">
              {isGroupChannel(channel) ? (
                <div className="w-12 h-12 bg-app-grey-30 rounded-full flex items-center justify-center overflow-hidden">
                  {channel.data?.image ? (
                    <img
                      src={channel.data.image}
                      alt="Group icon"
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <svg
                      className="w-6 h-6 text-app-grey-70"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  )}
                </div>
              ) : (
                <AppAvatar
                  size="lg"
                  type="mixed"
                  shape="Rounded"
                  text={getInitials({
                    firstName: members[0]?.user?.firstName,
                    lastName: members[0]?.user?.lastName,
                  })}
                />
              )}

              {channelMessageCounts > 0 && (
                <span className="absolute -top-1 -right-1 z-10 w-5 h-5 bg-app-primary-60 text-app-white-100 text-xs font-medium rounded-full flex items-center justify-center">
                  {channelMessageCounts > 99 ? '99+' : channelMessageCounts}
                </span>
              )}
            </div>

            {/* Message Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <h4 className="text-sm font-medium text-app-black-100 truncate">
                  {channel.data?.name || (members && members.length > 0 && getChannelName(members))}
                </h4>
                <span className="text-xs text-app-grey-55 flex-shrink-0">
                  {/* timestamp placeholder */}
                </span>
              </div>

              <div className="text-xs text-app-grey-60 truncate">{getLastMessage()}</div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MessagingChannelPreview
